/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { FormikProvider } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import type { InviteeInput } from '@sportninja/common/services/teamBalance'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import BaseModal from 'src/components/ts-components/BaseModal'
import { paragraphXSmall } from 'src/components/css'
import {
  FieldRow,
  TextField,
} from 'src/features/profile/player/setupModal/components/FormikField'
import { useInviteeForm } from '../hooks/useInviteeForm'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import { EMAIL_MAX_LENGTH, NAME_MAX_LENGTH } from '../utils/inviteForm'
import type { InviteFormMode, InviteRow } from '../types/slotInvite'

type InvitePlayersModalProps = {
  open: boolean
  mode: InviteFormMode
  /** `change` only — who the slot is held for now. */
  initialInvitee?: Partial<InviteRow> | null
  submitting: boolean
  /** Resolves on success (the caller closes); rejects with the API error. */
  onSubmit: (invitees: InviteeInput[]) => Promise<unknown>
  onClose: () => void
}

const TITLE_KEYS: Record<InviteFormMode, string> = {
  slot: 'common:balanceInviteModalTitleSlot',
  change: 'common:balanceInviteModalTitleChange',
}

const INTRO_KEYS: Record<InviteFormMode, string> = {
  slot: 'common:balanceInviteModalIntroSlot',
  change: 'common:balanceInviteModalIntroChange',
}

const SUBMIT_KEYS: Record<InviteFormMode, string> = {
  slot: 'common:balanceInviteModalSubmitSlot',
  change: 'common:save',
}

/** Below this list width a person's three fields stack instead of sharing a line. */
const INVITEE_FIELDS_COLLAPSE_AT = 600

/**
 * Jeff, Oct 1 — hold a Team Budget slot for one named person. They get an email to
 * register for the team, and until they do nobody else can take the slot.
 *
 * - `slot` — ⋮ Invite Player on an open slot.
 * - `change` — Change Invitee, starting from who it is held for now.
 *
 * Holding NEW slots for several people at once is Add Player Slots › Invite
 * Players (`AddPlayerSlotModal`), which runs the same form (`useInviteeForm`): the
 * rows are checked first, the server's refusals land on the field they name, and
 * anything that isn't about a field shows above the row.
 */
const InvitePlayersModal = (props: InvitePlayersModalProps) =>
  // Mounted only while open, so every open starts from a fresh row and no message.
  props.open ? <InvitePlayersForm {...props} /> : null

const InvitePlayersForm = ({
  open,
  mode,
  initialInvitee = null,
  submitting,
  onSubmit,
  onClose,
}: InvitePlayersModalProps) => {
  const { formik, formMessage } = useInviteeForm({
    initialInvitee: mode === 'change' ? initialInvitee : null,
    onSubmit,
  })

  return (
    <FormikProvider value={formik}>
      <BaseModal
        isOpen={open}
        onClose={onClose}
        title={balanceLabel(TITLE_KEYS[mode])}
        desktopWidth='720px'
        destroyOnClose
        actions={
          <div css={footerStyle}>
            <HeaderActionButton
              variant='secondary'
              text={balanceLabel('common:cancel')}
              onClick={onClose}
              disabled={submitting}
              data-testid='invite-cancel'
            />
            <HeaderActionButton
              variant='primary'
              text={balanceLabel(SUBMIT_KEYS[mode])}
              onClick={() => {
                formik.submitForm()
              }}
              isLoading={submitting || formik.isSubmitting}
              data-testid='invite-submit'
            />
          </div>
        }
      >
        <div css={bodyStyle}>
          <p css={introStyle}>{balanceText(INTRO_KEYS[mode])}</p>

          {formMessage ? (
            <p css={formErrorStyle} role='alert' data-testid='invite-error'>
              {formMessage}
            </p>
          ) : null}

          <div css={rowStyle}>
            <FieldRow
              template='1fr 1fr 1.4fr'
              collapseAt={INVITEE_FIELDS_COLLAPSE_AT}
            >
              <TextField
                name='invitees.0.name_first'
                label={balanceLabel('common:balanceInviteFirstName')}
                placeholder={balanceLabel(
                  'common:balanceInviteFirstNamePlaceholder'
                )}
                maxLength={NAME_MAX_LENGTH}
                required
              />
              <TextField
                name='invitees.0.name_last'
                label={balanceLabel('common:balanceInviteLastName')}
                placeholder={balanceLabel(
                  'common:balanceInviteLastNamePlaceholder'
                )}
                maxLength={NAME_MAX_LENGTH}
                required
              />
              <TextField
                name='invitees.0.email'
                label={balanceLabel('common:balanceInviteEmail')}
                placeholder={balanceLabel(
                  'common:balanceInviteEmailPlaceholder'
                )}
                maxLength={EMAIL_MAX_LENGTH}
                required
              />
            </FieldRow>
          </div>
        </div>
      </BaseModal>
    </FormikProvider>
  )
}

export default InvitePlayersModal

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`
const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const introStyle = css`
  ${paragraphXSmall};
  margin: 0;
  color: ${colors.NEUTRAL_40};
  line-height: 1.5;
`
const formErrorStyle = css`
  ${paragraphXSmall};
  margin: 0;
  color: ${colors.ERROR_200};
  line-height: 1.5;
`
/* FieldRow collapses on its CONTAINER's width, not the viewport's. The player
   setup modal's column is that container; in this dialog the row is. */
const rowStyle = css`
  container-type: inline-size;
`
